import {combine, forward, merge, sample} from 'effector';

import {RestError, showRestError} from 'core/errors';
import {initFiltersTable, initSortingsTable} from 'core/hooks/grid2.0';
import {mapFilters, mapSortings} from 'utils/grid-mappers';

import {
    clearDataCacheFx,
    data$,
    defaultFilters,
    defaultSortings,
    fetchDataFx,
    filters$,
    filtersChanged,
    filtersReset,
    Gate,
    gridCacheChanged,
    gridCacheReset,
    gridTokensCache$,
    pageNumber$,
    pageSize$,
    refetchWithSortByDescDate,
    search$,
    searchChanged,
    searchReset,
    sortings$,
    sortingsChanged,
    sortingsReset,
    stateReset,
    tableStateCacheChanged,
    tableStateChanged,
} from './index';

data$.on(fetchDataFx.done, (_, {result}) => result).reset(Gate.close);
pageNumber$.reset(filtersChanged, sortingsChanged, stateReset, searchChanged);
pageSize$.reset(stateReset);

gridTokensCache$
    .on(fetchDataFx.done, (state, {params, result}) => ({
        ...state,
        [params.paging.pageNumber + 1]: result.nextPageRef ?? null,
    }))
    .on(gridCacheReset, () => ({0: null}))
    .on(gridCacheChanged, (state, {pageNumber, token}) => ({
        ...state,
        [pageNumber]: token,
    }));

initFiltersTable(filters$, filtersChanged, filtersReset, defaultFilters, stateReset);
initSortingsTable(sortings$, sortingsChanged, sortingsReset, defaultSortings, stateReset);

forward({
    from: tableStateCacheChanged,
    to: clearDataCacheFx,
});

sample({
    clock: refetchWithSortByDescDate,
    fn: () => [{id: 'createdAt', value: true}],
    target: sortingsChanged,
});

forward({
    from: refetchWithSortByDescDate,
    to: searchReset,
});

sample({
    clock: merge([Gate.open, tableStateChanged, searchChanged]),
    source: combine({
        sortings: sortings$,
        pageNumber: pageNumber$,
        pageSize: pageSize$,
        filters: filters$,
        search: search$,
        cache: gridTokensCache$,
    }),
    fn: ({pageNumber, pageSize, filters, sortings, search, cache}) => ({
        paging: {
            pageNumber,
            pageSize,
        },
        filters: mapFilters(filters),
        sortings: mapSortings(sortings),
        search,
        pageRef: cache?.[pageNumber] ?? null,
    }),
    target: fetchDataFx,
});

fetchDataFx.fail.watch(({error}) => showRestError(error as RestError));
